import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { TeacherPicker } from "./TeacherPicker";

const errText = (err, fb) => err?.response?.data?.detail || fb;

const StudentRow = ({ s, onRemove }) => (
  <div className="flex items-center gap-3 p-2 rounded-2xl hover:bg-arc" data-testid={`class-student-${s.id}`}>
    <img src={s.avatar} alt="" className="w-9 h-9 rounded-full bg-arc object-cover" />
    <div className="flex-1 min-w-0">
      <div className="text-sm font-semibold text-arc-text truncate">{s.name}</div>
      <div className="text-xs text-arc-muted truncate">{s.email}{s.ra ? ` · RA ${s.ra}` : ""}</div>
    </div>
    <button type="button" onClick={() => onRemove(s)} data-testid={`remove-from-class-${s.id}`} className="text-arc-accent font-bold text-xs hover:underline">Remover</button>
  </div>
);

const AddStudents = ({ klass, students, classes, onAdded, setMsg }) => {
  const [sel, setSel] = useState([]);
  const [q, setQ] = useState("");
  const others = students.filter((s) => s.class_id !== klass.id && `${s.name || ""} ${s.email || ""} ${s.ra || ""}`.toLowerCase().includes(q.toLowerCase()));
  const cname = (id) => classes.find((c) => c.id === id)?.name || "Sem turma";
  const add = async () => {
    try {
      const { data } = await axios.post(`${API}/admin/classes/${klass.id}/students`, { student_ids: sel });
      setMsg({ ok: true, text: `✅ ${data.moved} aluno(s) adicionado(s) à turma.` });
      setSel([]); onAdded();
    } catch (err) { setMsg({ ok: false, text: `❌ ${errText(err, "Erro ao adicionar")}` }); }
  };
  return (
    <div className="mt-4">
      <div className="text-xs font-semibold text-arc-muted uppercase mb-2">Adicionar alunos</div>
      <input className="arc-input" placeholder="Buscar por nome, login ou RA" value={q} onChange={(e) => setQ(e.target.value)} data-testid="add-student-search" />
      <div className="max-h-48 overflow-y-auto mt-2 space-y-1" data-testid="add-student-list">
        {others.map((s) => (
          <label key={s.id} className="flex items-center gap-3 p-2 rounded-2xl hover:bg-arc cursor-pointer">
            <input type="checkbox" checked={sel.includes(s.id)} data-testid={`add-student-check-${s.id}`}
              onChange={() => setSel(sel.includes(s.id) ? sel.filter((x) => x !== s.id) : [...sel, s.id])} className="w-4 h-4" />
            <span className="text-sm text-arc-text flex-1 truncate">{s.name}</span>
            <span className="text-xs text-arc-muted">{cname(s.class_id)}</span>
          </label>
        ))}
        {others.length === 0 && <div className="text-xs text-arc-muted p-2">Nenhum aluno disponível.</div>}
      </div>
      <p className="text-xs text-arc-muted mt-2">Alunos de outra turma serão movidos para esta.</p>
      <button type="button" disabled={!sel.length} onClick={add} data-testid="add-students-btn" className="arc-btn arc-btn-secondary w-full mt-2 disabled:opacity-60">
        Adicionar {sel.length || ""} aluno(s)
      </button>
    </div>
  );
};

export const AdminClassEditor = ({ klass, teachers, classes, open, onClose, onSaved }) => {
  const [name, setName] = useState("");
  const [tids, setTids] = useState([]);
  const [members, setMembers] = useState([]);
  const [students, setStudents] = useState([]);
  const [msg, setMsg] = useState(null);

  const loadMembers = async () => {
    const [m, u] = await Promise.all([axios.get(`${API}/admin/classes/${klass.id}/students`), axios.get(`${API}/admin/users`)]);
    setMembers(m.data); setStudents(u.data.filter((x) => x.role === "student"));
  };
  useEffect(() => {
    if (!klass) return;
    setName(klass.name); setTids(klass.teacher_ids || []); setMsg(null); loadMembers();
  }, [klass]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/admin/classes/${klass.id}`, { name, teacher_ids: tids });
      setMsg({ ok: true, text: "✅ Turma atualizada!" }); onSaved();
    } catch (err) { setMsg({ ok: false, text: `❌ ${errText(err, "Erro ao salvar")}` }); }
  };
  const remove = async (s) => {
    try {
      await axios.delete(`${API}/admin/classes/${klass.id}/students/${s.id}`);
      loadMembers(); onSaved();
    } catch (err) { setMsg({ ok: false, text: `❌ ${errText(err, "Erro ao remover aluno")}` }); }
  };
  const destroy = async () => {
    if (!window.confirm(`Excluir a turma "${klass.name}"? Os alunos ficarão sem turma.`)) return;
    try {
      await axios.delete(`${API}/admin/classes/${klass.id}`);
      onSaved(); onClose();
    } catch (err) { setMsg({ ok: false, text: `❌ ${errText(err, "Erro ao excluir turma")}` }); }
  };
  const refresh = () => { loadMembers(); onSaved(); };

  if (!klass) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl" data-testid="edit-class-dialog">
        <DialogHeader>
          <DialogTitle className="font-fredoka text-2xl">Editar turma</DialogTitle>
          <DialogDescription>Nome, professores responsáveis e alunos da turma.</DialogDescription>
        </DialogHeader>
        {msg && <div data-testid="edit-class-msg" className={`p-3 rounded-2xl text-sm font-semibold ${msg.ok ? "bg-arc-success text-arc-text" : "bg-pink-100 text-arc-accent"}`}>{msg.text}</div>}
        <form onSubmit={save} className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-arc-muted uppercase">Nome da turma</label>
            <input required className="arc-input mt-1" value={name} onChange={(e) => setName(e.target.value)} data-testid="edit-class-name" />
          </div>
          <div>
            <label className="text-xs font-semibold text-arc-muted uppercase">Professores responsáveis</label>
            <TeacherPicker teachers={teachers} value={tids} onChange={setTids} testid="edit-class-teacher" />
          </div>
          <button type="submit" className="arc-btn arc-btn-primary w-full" data-testid="save-class-btn">Salvar turma</button>
        </form>
        <div className="mt-2">
          <div className="text-xs font-semibold text-arc-muted uppercase mb-2">Alunos na turma ({members.length})</div>
          <div className="max-h-56 overflow-y-auto space-y-1" data-testid="class-members-list">
            {members.map((s) => <StudentRow key={s.id} s={s} onRemove={remove} />)}
            {members.length === 0 && <div className="text-xs text-arc-muted p-2">Nenhum aluno nesta turma.</div>}
          </div>
          <AddStudents klass={klass} students={students} classes={classes} onAdded={refresh} setMsg={setMsg} />
        </div>
        <button type="button" onClick={destroy} data-testid="delete-class-btn" className="text-arc-accent font-bold text-sm hover:underline mt-2">🗑️ Excluir turma</button>
      </DialogContent>
    </Dialog>
  );
};
